import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ModalPicker, {
  PickerBrowser,
  PickerDialog,
  pickerIcon,
  pickerId,
  pickerName,
} from "components/admin/ModalPicker";
import GameIcon from "components/admin/GameIcon";
import api from "api/client";

/**
 * Loại quà theo PetRealmManager.GIFT_*. icon là ảnh trong assets của game, fallback là icon Material
 * khi loại quà lấy icon theo vật phẩm / pet / danh hiệu được chọn.
 */
export const GIFT_TYPES = [
  { type: 0, label: "Vàng", kind: "amount", icon: "gameMisc/currency_vang.png", hint: "Cộng vàng" },
  { type: 1, label: "Ngọc", kind: "amount", icon: "gameMisc/currency_ngoc.png", hint: "Cộng ngọc" },
  {
    type: 8,
    label: "Năng lượng",
    kind: "amount",
    icon: "gameMisc/currency_nluong.png",
    hint: "Cộng năng lượng",
  },
  {
    type: 7,
    label: "Exp pet",
    kind: "amount",
    fallback: "trending_up",
    hint: "Cộng exp cho pet đang theo người nhận",
  },
  {
    type: 11,
    label: "Điểm sự kiện",
    kind: "amount",
    icon: "gameMisc/event_0.png",
    hint: "Cộng điểm của sự kiện đang diễn ra",
  },
  {
    type: 12,
    label: "Quỹ bang",
    kind: "amount",
    icon: "gameMisc/icon_bang_hoi.png",
    hint: "Cộng vào quỹ bang hội của người nhận",
  },
  {
    type: 17,
    label: "Điểm đấu trường",
    kind: "amount",
    icon: "gameMisc/currency_chien.png",
    fallback: "emoji_events",
    hint: "Cộng điểm đấu trường thần thú, dùng mua ở shop đấu trường",
  },
  {
    type: 18,
    label: "Điểm lôi đài",
    kind: "amount",
    icon: "gameMisc/currency_diem.png",
    fallback: "sports_kabaddi",
    hint: "Cộng điểm lôi đài (điểm tích luỹ), dùng mua ở shop đấu trường sinh tử",
  },
  {
    type: 2,
    label: "Vật phẩm",
    kind: "item",
    fallback: "inventory_2",
    hint: "Tặng chắc chắn vật phẩm đã chọn",
  },
  {
    type: 19,
    label: "Trang bị",
    kind: "equip",
    fallback: "shield",
    hint: "Trang bị pet có sẵn cấp cường hoá, sao dung hợp, tuỳ chọn chỉ số tối đa",
  },
  {
    type: 20,
    label: "Cánh",
    kind: "wing",
    fallback: "flight",
    hint: "Cánh có sẵn cấp cường hoá, tuỳ chọn chỉ số tối đa, có thời hạn hoặc vĩnh viễn",
  },
  {
    type: 10,
    label: "Trang bị chỉ số tối đa (cũ)",
    kind: "item",
    fallback: "auto_awesome",
    hint: "Kiểu quà cũ, trang bị +0 chỉ số cao nhất. Quà mới dùng loại Trang bị",
    legacy: true,
  },
  {
    type: 16,
    label: "Ngọc khảm",
    kind: "gem",
    fallback: "diamond",
    hint: "Ngọc khảm có sẵn cấp cường hoá, vào túi ngọc của người nhận",
  },
  {
    type: "dust",
    label: "Bụi ngọc",
    kind: "dust",
    icon: "items/61047.png",
    hint: "Phân giải ngọc ra, dùng đổi ngọc và chuyển hệ ở THO SAN. Luôn khoá giao dịch",
  },
  {
    type: 4,
    label: "Tỉ lệ rơi",
    kind: "percent",
    fallback: "percent",
    hint: "Có tỉ lệ nhận được, cả gói chỉ rơi tối đa 1 món loại này",
  },
  {
    type: 9,
    label: "Rút ngẫu nhiên",
    kind: "random",
    fallback: "casino",
    hint: "Mỗi lượt rút trúng ngẫu nhiên 1 món trong danh sách",
  },
  {
    type: 14,
    label: "Cải trang",
    kind: "timedItem",
    fallback: "checkroom",
    hint: "Có thời hạn hoặc vĩnh viễn. Cánh có loại quà riêng (có cường hoá)",
  },
  {
    type: 13,
    label: "Danh hiệu",
    kind: "title",
    fallback: "military_tech",
    hint: "Có thời hạn hoặc vĩnh viễn",
  },
  {
    type: 15,
    label: "Pet",
    kind: "timedPet",
    fallback: "pets",
    hint: "Có thời hạn hoặc vĩnh viễn",
  },
];

const TYPE_BY_ID = Object.fromEntries(GIFT_TYPES.map((t) => [t.type, t]));

/** Id âm đặc biệt trong quà rút ngẫu nhiên: cộng điểm tích luỹ thay vì vật phẩm. */
const POINTS_ID = -126;

/** PetRealmManager.GIFT_PET_LEVEL_INDEX: cấp pet nằm sau các ô phút, giờ, ngày, tháng, năm. */
const PET_LEVEL_INDEX = 8;

/** PetRealmManager.ITEM_GEM và MAX_LVL_ENCHANT_GEM. */
export const GEM_ITEM_TYPE = 17;
export const MAX_GEM_LEVEL = 10;

/** PetRealmManager.ITEM_GEM_DUST_ID: Bụi ngọc, tặng bằng quà vật phẩm [2, 301, số lượng, 0] (luôn khoá giao dịch). */
export const GEM_DUST_ID = 301;

const isDustLine = (line) => line[0] === 2 && line[1] === GEM_DUST_ID;

/** Loại vật phẩm theo PetRealmManager: trang bị pet (vũ khí, giáp, nón, giày, găng), cải trang, cánh. */
const EQUIP_ITEM_TYPES = [1, 2, 3, 104, 105];
const SKIN_ITEM_TYPE = 4;
const WING_ITEM_TYPE = 5;
/** Ô thứ 8 trở đi của quà trang bị không có nghĩa, ít hơn thì máy chủ dùng mặc định. */
const EQUIP_LINE_LENGTH = 7;
/** PetRealmManager.GIFT_WING_EXPIRE_INDEX: quà cánh [20, id, sl, cấp, tối đa, GD, phút, giờ, ngày]. */
const WING_EXPIRE_INDEX = 6;
/** PetRealmManager.MAX_LVL_ENCHANT_WING, dùng khi chưa tải được món. */
const WING_MAX_ENCHANT = 10;

/**
 * Đơn vị thời hạn của quà có hạn, index là vị trí ô trong dòng quà (sau ô vĩnh viễn).
 * Cải trang máy chủ chỉ đọc tới ngày; danh hiệu và pet đọc tới năm.
 */
const DURATION_UNITS = [
  { key: "minute", label: "phút", index: 3 },
  { key: "hour", label: "giờ", index: 4 },
  { key: "day", label: "ngày", index: 5 },
  { key: "month", label: "tháng", index: 6 },
  { key: "year", label: "năm", index: 7 },
];

const unitsFor = (kind) =>
  kind === "timedItem" || kind === "wing" ? DURATION_UNITS.slice(0, 3) : DURATION_UNITS;

/** Cú pháp từng loại quà, hiện trong chế độ nhập JSON. */
const GIFT_SYNTAX = [
  { type: 0, format: "[0, số vàng]" },
  { type: 1, format: "[1, số ngọc]" },
  { type: 8, format: "[8, số năng lượng]" },
  { type: 7, format: "[7, số exp]" },
  { type: 11, format: "[11, số điểm]" },
  { type: 12, format: "[12, số vàng vào quỹ]" },
  { type: 17, format: "[17, số điểm đấu trường]" },
  { type: 18, format: "[18, số điểm lôi đài]" },
  { type: 2, format: "[2, id vật phẩm, số lượng]", note: "thêm 0 ở cuối để khoá giao dịch" },
  {
    type: 19,
    format: "[19, id, số lượng, cấp cường hoá, sao dung hợp, 1 = chỉ số tối đa, 0 = khoá GD]",
    note: "trang bị pet, dung hợp cần +10 và trang bị lev cuối",
  },
  {
    type: 20,
    format: "[20, id, số lượng, cấp 0-10, 1 = chỉ số tối đa, 0 = khoá GD, phút, giờ, ngày]",
    note: "hạn đều 0 hoặc bỏ trống = vĩnh viễn",
  },
  { type: 10, format: "[10, id vật phẩm, số lượng]", note: "kiểu cũ, trang bị +0 chỉ số tối đa" },
  {
    type: 16,
    format: "[16, id ngọc, số lượng, cấp 0-10]",
    note: "thêm 0 ở cuối để khoá giao dịch",
  },
  {
    type: "dust",
    format: `[2, ${GEM_DUST_ID}, số lượng, 0]`,
    note: "quà vật phẩm, luôn khoá giao dịch",
  },
  {
    type: 4,
    format: "[4, id vật phẩm, tỉ lệ x100, số lượng]",
    note: "500 = 5%, cả gói chỉ rơi tối đa 1 món loại này",
  },
  {
    type: 9,
    format: "[9, số lượt, SL, id, SL, id, ...]",
    note: "mỗi lượt rơi ngẫu nhiên 1 cặp (SL, id); id âm là nhóm vật phẩm",
  },
  {
    type: 14,
    format: "[14, id, 1] | [14, id, 0, phút, giờ, ngày]",
    note: "1 = vĩnh viễn",
  },
  { type: 13, format: "[13, id danh hiệu, 1] | [13, id, 0, phút, giờ, ngày, tháng, năm]" },
  {
    type: 15,
    format: "[15, id pet, 1] | [15, id pet, 0, phút, giờ, ngày, tháng, năm]",
    note: "cấp pet ở vị trí thứ 9: [15, id, 1, 0, 0, 0, 0, 0, cấp]",
  },
];

const SAMPLE_GIFT = [
  [0, 100000],
  [1, 500],
  [8, 50],
  [2, 1, 10],
  [2, 187, 5, 0],
  [19, 35, 1, 10, 0, 1],
  [16, 155, 1, 5],
  [2, GEM_DUST_ID, 30, 0],
  [4, 182, 500, 1],
  [9, 2, 1, 183, 3, 1, 1, -130],
  [14, 1000016, 0, 0, 0, 7],
  [13, 1, 1],
  [15, 1, 0, 0, 0, 3],
];

const SAMPLE_TEXT = `[\n${SAMPLE_GIFT.map((line) => `  ${JSON.stringify(line)}`).join(",\n")}\n]`;

function poolKind(id) {
  if (id === POINTS_ID) return "points";
  return id < 0 ? "group" : "item";
}

/** Chuyển một dòng quà dạng mảng sang dòng để sửa trên form; dòng không hỗ trợ giữ nguyên JSON. */
function toRow(line) {
  if (isDustLine(line)) return { type: "dust", amount: line[2] ?? 1 };
  const meta = TYPE_BY_ID[line[0]];
  if (!meta) return { type: "raw", raw: JSON.stringify(line) };
  switch (meta.kind) {
    case "amount":
      return line.length === 2
        ? { type: line[0], amount: line[1] }
        : { type: "raw", raw: JSON.stringify(line) };
    case "item":
      return {
        type: line[0],
        id: line[1],
        amount: line[2] ?? 1,
        noTrade: line[0] === 2 && line[3] === 0,
      };
    case "gem":
      return {
        type: line[0],
        id: line[1],
        amount: line[2] ?? 1,
        level: line[3] ?? 0,
        noTrade: line[4] === 0,
      };
    case "wing": {
      // Hạn ở 3 ô phút, giờ, ngày từ WING_EXPIRE_INDEX; gộp nhiều đơn vị thì giữ JSON
      const units = unitsFor("wing");
      const set = units.filter((u, i) => line[WING_EXPIRE_INDEX + i]);
      if (line.length > WING_EXPIRE_INDEX + units.length || set.length > 1) {
        return { type: "raw", raw: JSON.stringify(line) };
      }
      const unit = set[0] || units[2];
      return {
        type: line[0],
        id: line[1],
        amount: line[2] ?? 1,
        level: line[3] || 0,
        maxOption: line[4] === 1,
        noTrade: line[5] === 0,
        permanent: set.length === 0,
        duration: set[0] ? line[WING_EXPIRE_INDEX + units.indexOf(unit)] : 7,
        unit: unit.key,
      };
    }
    case "equip":
      if (line.length > EQUIP_LINE_LENGTH) return { type: "raw", raw: JSON.stringify(line) };
      return {
        type: line[0],
        id: line[1],
        amount: line[2] ?? 1,
        level: line[3] || 0,
        fusion: line[4] || 0,
        maxOption: line[5] === 1,
        noTrade: line[6] === 0,
      };
    case "percent":
      return { type: line[0], id: line[1], percent: (line[2] || 0) / 100, amount: line[3] ?? 1 };
    case "random": {
      const pool = [];
      for (let i = 2; i + 1 < line.length; i += 2) {
        pool.push({ kind: poolKind(line[i + 1]), id: line[i + 1], count: line[i] });
      }
      return { type: line[0], draws: line[1] || 1, pool };
    }
    default: {
      const permanent = line[2] === 1;
      const isPet = meta.kind === "timedPet";
      const units = unitsFor(meta.kind);
      const set = units.filter((u) => line[u.index]);
      // Thời hạn gộp nhiều đơn vị, ô ngoài phần form hiểu được, hay hạn cố định của danh hiệu mùa
      // thì giữ nguyên JSON.
      const extra = line.some(
        (n, i) =>
          n && i > 2 && !units.some((u) => u.index === i) && !(isPet && i === PET_LEVEL_INDEX)
      );
      if (extra || (permanent ? set.length > 0 : set.length > 1)) {
        return { type: "raw", raw: JSON.stringify(line) };
      }
      const unit = set[0] || units.find((u) => u.key === "day");
      const row = {
        type: line[0],
        id: line[1],
        permanent,
        duration: set[0] ? line[unit.index] : 7,
        unit: unit.key,
      };
      if (isPet) row.level = line[PET_LEVEL_INDEX] || 1;
      return row;
    }
  }
}

function toLine(row) {
  if (row.type === "raw") {
    try {
      const parsed = JSON.parse(row.raw);
      return Array.isArray(parsed) ? parsed.map(Number) : null;
    } catch (e) {
      return null;
    }
  }
  const meta = TYPE_BY_ID[row.type];
  switch (meta.kind) {
    case "amount":
      return [row.type, Number(row.amount) || 0];
    case "item": {
      const line = [row.type, Number(row.id) || 0, Number(row.amount) || 0];
      if (row.type === 2 && row.noTrade) line.push(0);
      return line;
    }
    case "gem": {
      const line = [row.type, Number(row.id) || 0, Number(row.amount) || 0, Number(row.level) || 0];
      if (row.noTrade) line.push(0);
      return line;
    }
    case "wing": {
      const line = [
        row.type,
        Number(row.id) || 0,
        Number(row.amount) || 0,
        Number(row.level) || 0,
        row.maxOption ? 1 : 0,
        row.noTrade ? 0 : 1,
      ];
      if (!row.permanent) {
        const units = unitsFor("wing");
        const found = units.findIndex((u) => u.key === row.unit);
        line.push(0, 0, 0);
        line[WING_EXPIRE_INDEX + (found < 0 ? 2 : found)] = Number(row.duration) || 0;
      }
      return line;
    }
    case "equip":
      return [
        row.type,
        Number(row.id) || 0,
        Number(row.amount) || 0,
        Number(row.level) || 0,
        Number(row.fusion) || 0,
        row.maxOption ? 1 : 0,
        row.noTrade ? 0 : 1,
      ];
    case "dust":
      return [2, GEM_DUST_ID, Number(row.amount) || 0, 0];
    case "percent":
      return [
        row.type,
        Number(row.id) || 0,
        Math.round((Number(row.percent) || 0) * 100),
        Number(row.amount) || 1,
      ];
    case "random":
      return [
        row.type,
        Number(row.draws) || 1,
        ...(row.pool || [])
          .filter((p) => p.id)
          .flatMap((p) => [Number(p.count) || 1, Number(p.id)]),
      ];
    default: {
      const line = [row.type, Number(row.id) || 0, row.permanent ? 1 : 0];
      if (!row.permanent) {
        const unit = unitsFor(meta.kind).find((u) => u.key === row.unit) || DURATION_UNITS[2];
        while (line.length < Math.max(6, unit.index + 1)) line.push(0);
        line[unit.index] = Number(row.duration) || 0;
      }
      const level = Number(row.level) || 1;
      if (meta.kind === "timedPet" && level > 1) {
        while (line.length < PET_LEVEL_INDEX) line.push(0);
        line.push(level);
      }
      return line;
    }
  }
}

function durationText(line) {
  if (line[2] === 1) return "vĩnh viễn";
  const parts = DURATION_UNITS.filter((u) => line[u.index]).map(
    (u) => `${line[u.index]} ${u.label}`
  );
  return parts.length ? parts.join(" ") : "0 ngày";
}

export function describeGift(gift) {
  if (!Array.isArray(gift) || gift.length === 0) return "Không có quà";
  return gift
    .map((line) => {
      if (isDustLine(line)) return `Bụi ngọc x${line[2]}`;
      const meta = TYPE_BY_ID[line[0]];
      if (!meta) return JSON.stringify(line);
      switch (meta.kind) {
        case "amount":
          return `${meta.label} ${Number(line[1]).toLocaleString("vi-VN")}`;
        case "item":
          return `${meta.label} #${line[1]} x${line[2]}`;
        case "gem":
          return `${meta.label} #${line[1]}${line[3] > 0 ? ` +${line[3]}` : ""} x${line[2]}`;
        case "wing":
          return `${meta.label} #${line[1]}${line[3] > 0 ? ` +${line[3]}` : ""} x${line[2]}`;
        case "equip":
          return `${meta.label} #${line[1]}${line[3] > 0 ? ` +${line[3]}` : ""}${
            line[4] > 0 ? ` ★${line[4]}` : ""
          } x${line[2]}`;
        case "percent":
          return `${(line[2] || 0) / 100}% rơi #${line[1]} x${line[3] ?? 1}`;
        case "random":
          return `${meta.label} ${line[1]} lượt`;
        case "timedPet":
          return `${meta.label} #${line[1]}${
            line[PET_LEVEL_INDEX] > 1 ? ` cấp ${line[PET_LEVEL_INDEX]}` : ""
          } ${durationText(line)}`;
        default:
          return `${meta.label} #${line[1]} ${durationText(line)}`;
      }
    })
    .join(", ");
}

const giftLineShape = PropTypes.shape({
  type: PropTypes.number,
  text: PropTypes.string.isRequired,
  icon: PropTypes.string,
  fallback: PropTypes.string,
  pool: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string, icon: PropTypes.string })),
});

function GiftChip({ line }) {
  const chip = (
    <Chip
      variant="outlined"
      label={line.pool ? `${line.text} ▾` : line.text}
      icon={
        <MDBox display="inline-flex" pl={0.5}>
          <GameIcon path={line.icon} size={22} fallback={line.fallback || "redeem"} />
        </MDBox>
      }
      sx={{ height: 32, maxWidth: "100%" }}
    />
  );
  if (!line.pool) return chip;
  return (
    <Tooltip
      title={
        <MDBox py={0.5}>
          {line.pool.map((part, index) => (
            <MDBox key={index} display="flex" alignItems="center" gap={1} py={0.25}>
              <GameIcon path={part.icon} size={20} fallback="redeem" />
              <MDTypography variant="caption" color="white" textAlign="left">
                {part.text}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      }
    >
      {chip}
    </Tooltip>
  );
}

GiftChip.propTypes = { line: giftLineShape.isRequired };

/**
 * Danh sách quà kèm tên + icon giống trong game. Truyền lines (đã diễn giải từ máy chủ) hoặc gift
 * (mảng quà thô, sẽ gọi máy chủ để lấy tên vật phẩm / pet / danh hiệu).
 */
export function GiftPreview({ gift, lines }) {
  const [described, setDescribed] = useState(lines);
  const giftKey = JSON.stringify(gift || []);

  useEffect(() => {
    if (lines) {
      setDescribed(lines);
      return undefined;
    }
    if (!Array.isArray(gift) || gift.length === 0) {
      setDescribed([]);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      api
        .post("/api/admin/templates/describe-gift", gift)
        .then((res) => active && setDescribed(res))
        .catch(() => active && setDescribed(null));
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [giftKey, lines]);

  if (lines ? lines.length === 0 : !Array.isArray(gift) || gift.length === 0) {
    return (
      <MDTypography variant="caption" color="text">
        Không có quà
      </MDTypography>
    );
  }
  if (!described) {
    return (
      <MDTypography variant="caption" color="text">
        {describeGift(gift)}
      </MDTypography>
    );
  }
  return (
    <MDBox display="flex" gap={1} flexWrap="wrap">
      {described.map((line, index) => (
        <GiftChip key={index} line={line} />
      ))}
    </MDBox>
  );
}

GiftPreview.defaultProps = { gift: null, lines: null };
GiftPreview.propTypes = {
  gift: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  lines: PropTypes.arrayOf(giftLineShape),
};

function JsonHelp({ onInsertSample }) {
  return (
    <MDBox mt={1} p={1.5} borderRadius="md" sx={{ bgcolor: "action.hover" }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="button" fontWeight="medium">
          JSON mẫu
        </MDTypography>
        <MDBox display="flex" gap={1}>
          <MDButton size="small" variant="outlined" color="info" onClick={onInsertSample}>
            <Icon>content_paste_go</Icon>&nbsp;Dùng mẫu này
          </MDButton>
          <MDButton
            size="small"
            variant="text"
            color="dark"
            onClick={() => navigator.clipboard?.writeText(SAMPLE_TEXT)}
          >
            <Icon>content_copy</Icon>&nbsp;Sao chép
          </MDButton>
        </MDBox>
      </MDBox>
      <MDBox
        component="pre"
        m={0}
        mb={1.5}
        sx={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" }}
      >
        {SAMPLE_TEXT}
      </MDBox>
      <MDBox mb={1.5}>
        <GiftPreview gift={SAMPLE_GIFT} />
      </MDBox>
      <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
        Cú pháp từng loại
      </MDTypography>
      {GIFT_SYNTAX.map((s) => {
        const meta = TYPE_BY_ID[s.type];
        return (
          <MDBox key={s.type} display="flex" alignItems="center" gap={1} py={0.25}>
            <GameIcon path={meta.icon} size={20} fallback={meta.fallback} />
            <MDTypography variant="caption" fontWeight="medium" sx={{ minWidth: 150 }}>
              {meta.label}
            </MDTypography>
            <MDTypography variant="caption" sx={{ fontFamily: "monospace" }}>
              {s.format}
            </MDTypography>
            {s.note && (
              <MDTypography variant="caption" color="text">
                ({s.note})
              </MDTypography>
            )}
          </MDBox>
        );
      })}
    </MDBox>
  );
}

JsonHelp.propTypes = { onInsertSample: PropTypes.func.isRequired };

function TypeLabel({ meta }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      <GameIcon path={meta.icon} size={20} fallback={meta.fallback} />
      {meta.label}
    </MDBox>
  );
}

TypeLabel.propTypes = {
  meta: PropTypes.shape({
    icon: PropTypes.string,
    fallback: PropTypes.string,
    label: PropTypes.string.isRequired,
  }).isRequired,
};

const POOL_KINDS = [
  { value: "item", label: "Vật phẩm", fallback: "inventory_2" },
  { value: "group", label: "Nhóm", fallback: "category" },
  { value: "points", label: "Điểm", icon: "gameMisc/currency_diem.png", fallback: "stars" },
];

/** Danh sách món của quà rút ngẫu nhiên, mỗi lượt rút trúng 1 món với tỉ lệ bằng nhau. */
function RandomPool({ row, onChange }) {
  const pool = row.pool || [];
  const [picking, setPicking] = useState(false);
  const setPool = (next) => onChange({ pool: next });
  const addPicked = (list, count) => {
    setPicking(false);
    setPool([
      ...pool.filter((entry) => entry.id),
      ...list.map((item) => ({ kind: "item", id: pickerId("item", item), count, item })),
    ]);
  };
  const updateEntry = (index, patch) =>
    setPool(pool.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  const changeKind = (index, kind) => {
    const ids = { item: null, group: null, points: POINTS_ID };
    updateEntry(index, { kind, id: ids[kind] });
  };
  const filled = pool.filter((entry) => entry.id).length;
  const chance = filled
    ? `${(100 / filled).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}%`
    : "";

  return (
    <MDBox
      pl={{ xs: 0, sm: 2 }}
      ml={{ xs: 0, sm: 1 }}
      sx={{ borderLeft: { sm: "2px dashed" }, borderColor: { sm: "divider" } }}
    >
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        Mỗi lượt rút trúng ngẫu nhiên 1 món trong danh sách dưới đây, các món có tỉ lệ bằng nhau
        {chance && ` (mỗi món ${chance})`}.
      </MDTypography>
      {pool.map((entry, index) => (
        <Grid container spacing={1} alignItems="flex-start" key={index} mb={1}>
          <Grid item xs={12} sm={4}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={entry.kind}
              onChange={(event, kind) => kind && changeKind(index, kind)}
              sx={{ height: 44 }}
            >
              {POOL_KINDS.map((k) => (
                <ToggleButton key={k.value} value={k.value} sx={{ px: 0.5 }}>
                  <TypeLabel meta={k} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} sm={8}>
            {entry.kind === "item" && (
              <ModalPicker
                kind="item"
                label="Vật phẩm"
                value={entry.id}
                item={entry.item}
                onChange={(id, item) => updateEntry(index, { id, item })}
              />
            )}
            {entry.kind === "group" && (
              <ModalPicker
                kind="group"
                label="Nhóm vật phẩm (rơi 1 món bất kỳ trong nhóm)"
                value={entry.id}
                onChange={(id) => updateEntry(index, { id })}
              />
            )}
            {entry.kind === "points" && (
              <MDBox display="flex" alignItems="center" height={44}>
                <MDTypography variant="caption" color="text">
                  Cộng điểm tích luỹ cho người nhận
                </MDTypography>
              </MDBox>
            )}
          </Grid>
          <Grid item xs={8} sm={3}>
            <MDInput
              fullWidth
              type="number"
              label={entry.kind === "points" ? "Số điểm" : "Số lượng"}
              value={entry.count}
              inputProps={{ min: 1 }}
              onChange={(event) => updateEntry(index, { count: event.target.value })}
            />
          </Grid>
          <Grid item xs={2} sm={1}>
            <MDBox display="flex" alignItems="center" height={44}>
              <MDTypography variant="caption" color="text">
                {entry.id ? chance : ""}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={2} sm={1}>
            <Tooltip title="Bỏ món này">
              <span>
                <IconButton
                  color="error"
                  disabled={pool.length <= 1}
                  onClick={() => setPool(pool.filter((e, i) => i !== index))}
                >
                  <Icon>remove_circle_outline</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      ))}
      <MDBox display="flex" flexWrap="wrap" gap={1}>
        <MDButton variant="outlined" color="info" size="small" onClick={() => setPicking(true)}>
          <Icon>library_add</Icon>&nbsp;Chọn nhiều vật phẩm
        </MDButton>
        <MDButton
          variant="text"
          color="info"
          size="small"
          onClick={() => setPool([...pool, { kind: "item", id: null, count: 1 }])}
        >
          <Icon>add</Icon>&nbsp;Thêm 1 dòng (nhóm, điểm...)
        </MDButton>
      </MDBox>
      {picking && (
        <PickerDialog
          multiple
          kind="item"
          amountLabel="Số lượng mỗi món"
          existing={pool.filter((e) => e.kind === "item" && e.id).map((e) => e.id)}
          onConfirm={addPicked}
          onClose={() => setPicking(false)}
        />
      )}
    </MDBox>
  );
}

RandomPool.propTypes = {
  row: PropTypes.shape({
    pool: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      })
    ),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

const ADD_GROUPS = [
  { label: "Tiền tệ, chỉ số", kinds: ["amount"] },
  {
    label: "Vật phẩm, trang bị",
    kinds: ["item", "equip", "wing", "gem", "dust", "percent", "random"],
  },
  { label: "Cải trang, danh hiệu, pet", kinds: ["timedItem", "title", "timedPet"] },
];

function newRow(type) {
  const meta = TYPE_BY_ID[type];
  switch (meta.kind) {
    case "amount":
      return { type, amount: 1000 };
    case "item":
      return { type, id: null, amount: 1 };
    case "gem":
      return { type, id: null, amount: 1, level: 0 };
    case "equip":
      return { type, id: null, amount: 1, level: 0, fusion: 0, maxOption: false };
    case "wing":
      return {
        type,
        id: null,
        amount: 1,
        level: 0,
        maxOption: false,
        permanent: true,
        duration: 7,
        unit: "day",
      };
    case "dust":
      return { type, amount: 30 };
    case "percent":
      return { type, id: null, percent: 5, amount: 1 };
    case "random":
      return { type, draws: 1, pool: [{ kind: "item", id: null, count: 1 }] };
    case "timedPet":
      return { type, id: null, permanent: true, duration: 7, unit: "day", level: 1 };
    default:
      return { type, id: null, permanent: true, duration: 7, unit: "day" };
  }
}

/** Loại quà cần chọn món trong bảng: nguồn của bảng chọn, chọn được nhiều món một lần. */
const PICK_SOURCES = {
  // Vật phẩm thường: bỏ trang bị, cải trang, cánh, ngọc vì đã có loại quà riêng đủ thông số
  2: {
    kind: "item",
    itemFilter: { notTypes: [...EQUIP_ITEM_TYPES, SKIN_ITEM_TYPE, WING_ITEM_TYPE, GEM_ITEM_TYPE] },
  },
  10: { kind: "item", itemFilter: { types: EQUIP_ITEM_TYPES } },
  19: { kind: "item", itemFilter: { types: EQUIP_ITEM_TYPES } },
  16: { kind: "item", itemType: GEM_ITEM_TYPE },
  4: { kind: "item" },
  14: { kind: "item", itemFilter: { types: [SKIN_ITEM_TYPE] } },
  20: { kind: "item", itemFilter: { types: [WING_ITEM_TYPE] } },
  13: { kind: "title" },
  15: { kind: "pet" },
};

const numberText = (n) => (Number(n) || 0).toLocaleString("vi-VN");

/**
 * Kẹp thiết lập đặt chung vào giới hạn của từng món trang bị: cánh chỉ tới +10, món không dung hợp
 * được hay chưa đủ cấp thì về 0 sao. Loại quà khác giữ nguyên.
 */
function fitRow(row) {
  const { item } = row;
  const kind = TYPE_BY_ID[row.type]?.kind;
  if (kind === "wing") {
    const max = item?.maxEnchant ?? WING_MAX_ENCHANT;
    return { ...row, level: Math.min(Number(row.level) || 0, max) };
  }
  if (kind !== "equip" || !item) return row;
  const level = Math.min(Number(row.level) || 0, item.maxEnchant ?? Infinity);
  const canFuse = item.maxFusion > 0 && level >= (item.fusionMinLvl ?? 10);
  const fusion = canFuse ? Math.min(Number(row.fusion) || 0, item.maxFusion) : 0;
  return { ...row, level, fusion };
}

/** Thiết lập chung của một loại quà trong dialog thêm quà: bỏ id, giữ số lượng, cấp, hạn... */
function defaultsFor(type) {
  const { id, ...rest } = newRow(type);
  return rest;
}

/**
 * Mọi ô nhập máy chủ đọc được của một loại quà (trừ ô chọn món): số lượng, cấp, khoá giao dịch,
 * tỉ lệ, thời hạn theo phút / giờ / ngày / tháng / năm. Dùng chung cho dòng quà và giỏ thêm quà.
 * bulk: đang đặt chung cho nhiều món, nhãn số lượng ghi "mỗi món".
 */
function GiftFields({ meta, value, onChange, bulk, bulkFusion }) {
  const num = (key, label, opts = {}) => (
    <MDInput
      key={key}
      type="number"
      label={label}
      value={value[key] ?? ""}
      onChange={(event) => onChange({ [key]: event.target.value })}
      sx={{ width: opts.width || 120 }}
      inputProps={{ min: opts.min ?? 1, max: opts.max, step: opts.step }}
      helperText={opts.helperText}
      error={Boolean(opts.error)}
      disabled={Boolean(opts.disabled)}
    />
  );
  const toggle = (key, label) => (
    <MDBox key={key} display="flex" alignItems="center" height={44}>
      <Switch
        checked={Boolean(value[key])}
        onChange={(event) => onChange({ [key]: event.target.checked })}
      />
      <MDTypography variant="caption" sx={{ whiteSpace: "nowrap" }}>
        {label}
      </MDTypography>
    </MDBox>
  );
  const note = (text) => (
    <MDBox key="note" display="flex" alignItems="center" height={44}>
      <MDTypography variant="caption" color="text">
        {text}
      </MDTypography>
    </MDBox>
  );
  // Vĩnh viễn hoặc thời hạn theo một đơn vị (cải trang, cánh: tới ngày; danh hiệu, pet: tới năm)
  const durationFields = () => {
    const units = unitsFor(meta.kind);
    return [
      toggle("permanent", "Vĩnh viễn"),
      !value.permanent && num("duration", "Thời hạn", { width: 100 }),
      !value.permanent && (
        <MDInput
          key="unit"
          select
          label="Đơn vị"
          value={units.some((u) => u.key === value.unit) ? value.unit : "day"}
          onChange={(event) => onChange({ unit: event.target.value })}
          SelectProps={{ sx: { height: 44 } }}
          sx={{ width: 100 }}
        >
          {units.map((u) => (
            <MenuItem key={u.key} value={u.key}>
              {u.label}
            </MenuItem>
          ))}
        </MDInput>
      ),
    ].filter(Boolean);
  };
  const amountLabel = bulk ? "Số lượng mỗi món" : "Số lượng";
  if (meta.kind === "equip") {
    // value.item có khi đã biết món (vừa chọn, hoặc ô chọn tải xong): biết cấp tối đa và có dung hợp được không.
    // Đặt chung (bulk) thì bulkFusion là số sao tối đa của các món dung hợp được trong giỏ.
    const { item } = value;
    const maxEnchant = item?.maxEnchant;
    const maxFusion = bulk ? bulkFusion : item?.maxFusion;
    const fusionMinLvl = item?.fusionMinLvl ?? 10;
    const level = Number(value.level) || 0;
    const fusion = Number(value.fusion) || 0;
    const levelError = maxEnchant != null && level > maxEnchant;
    // Chưa biết món (đang tải) thì chỉ hiện khi dòng quà đã có sao, tránh mất dữ liệu đang có
    const showFusion = maxFusion != null ? maxFusion > 0 : fusion > 0;
    const fusionLow = fusion > 0 && level < fusionMinLvl;
    let fusionHelp = null;
    if (fusionLow) fusionHelp = `Cần cường hoá từ +${fusionMinLvl}`;
    else if (bulk) fusionHelp = "Chỉ áp cho món dung hợp được";
    return [
      num("amount", amountLabel, { width: 140, max: 100 }),
      num("level", maxEnchant != null ? `Cường hoá 0-${maxEnchant}` : "Cường hoá +", {
        min: 0,
        max: maxEnchant,
        width: 130,
        error: levelError,
        helperText: levelError ? `Tối đa +${maxEnchant}` : null,
      }),
      showFusion &&
        num("fusion", maxFusion ? `Dung hợp 0-${maxFusion}★` : "Dung hợp ★", {
          min: 0,
          max: maxFusion,
          width: 130,
          error: fusionLow || (maxFusion != null && fusion > maxFusion),
          helperText: fusionHelp,
        }),
      toggle("maxOption", "Chỉ số gốc tối đa"),
      toggle("noTrade", "Không cho giao dịch"),
    ].filter(Boolean);
  }
  switch (meta.kind) {
    case "amount":
      return num("amount", `Số ${meta.label.toLowerCase()}`, {
        min: 0,
        width: 200,
        helperText: `= ${numberText(value.amount)}`,
      });
    case "dust":
      return num("amount", "Số bụi", {
        width: 200,
        helperText: "Đổi 1 ngọc bậc 1: 30 bụi, bóng tối / ánh sáng: 100 bụi",
      });
    case "item":
      return [
        num("amount", amountLabel, { width: 140 }),
        meta.type === 2
          ? toggle("noTrade", "Không cho giao dịch")
          : note("Công, thủ, máu, mana ra mức cao nhất của món"),
      ];
    case "gem":
      return [
        num("amount", amountLabel, { width: 140, max: 100 }),
        num("level", `Cấp 0-${MAX_GEM_LEVEL}`, { min: 0, max: MAX_GEM_LEVEL, width: 100 }),
        toggle("noTrade", "Không cho giao dịch"),
      ];
    case "percent":
      return [
        num("percent", "Tỉ lệ rơi (%)", { min: 0, max: 100, step: 0.01 }),
        num("amount", amountLabel, { width: 140 }),
      ];
    case "random":
      return num("draws", "Số lượt rút");
    case "wing": {
      const maxEnchant = value.item?.maxEnchant ?? WING_MAX_ENCHANT;
      const level = Number(value.level) || 0;
      return [
        num("amount", amountLabel, { width: 140, max: 100 }),
        num("level", `Cường hoá 0-${maxEnchant}`, {
          min: 0,
          max: maxEnchant,
          width: 130,
          error: level > maxEnchant,
          helperText: level > maxEnchant ? `Tối đa +${maxEnchant}` : null,
        }),
        toggle("maxOption", "Chỉ số gốc tối đa"),
        toggle("noTrade", "Không cho giao dịch"),
        ...durationFields(),
      ];
    }
    default:
      return [
        meta.kind === "timedPet" && num("level", "Cấp pet", { width: 100 }),
        ...durationFields(),
      ].filter(Boolean);
  }
}

GiftFields.defaultProps = { bulk: false, bulkFusion: 0 };
GiftFields.propTypes = {
  meta: PropTypes.shape({
    type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    kind: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  value: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    permanent: PropTypes.bool,
    unit: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  bulk: PropTypes.bool,
  /** Đặt chung trang bị: số sao tối đa của các món dung hợp được trong giỏ, 0 thì ẩn ô dung hợp. */
  bulkFusion: PropTypes.number,
};

/** Một món trong giỏ của dialog thêm quà: icon, tên, đủ ô nhập để chỉnh riêng món đó. */
function CartRow({ row, onChange, onRemove, onFocus }) {
  const meta = TYPE_BY_ID[row.type];
  const pick = PICK_SOURCES[row.type];
  const icon = row.item ? pickerIcon(pick.kind, row.item) : meta.icon;
  const name = row.item ? pickerName(pick.kind, row.item) : meta.label;
  return (
    <MDBox border="1px solid" borderColor="divider" borderRadius="md" p={1} mb={1}>
      <MDBox display="flex" alignItems="center" gap={1}>
        <GameIcon path={icon} size={24} fallback={meta.fallback} />
        <MDBox
          flexGrow={1}
          minWidth={0}
          lineHeight={1.1}
          onClick={onFocus}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="caption" fontWeight="medium" display="block" noWrap>
            {name}
            {row.id != null && ` #${row.id}`}
          </MDTypography>
          {row.item && (
            <MDTypography variant="caption" color="text" sx={{ fontSize: 11 }}>
              {meta.label}
            </MDTypography>
          )}
        </MDBox>
        <IconButton size="small" color="error" onClick={onRemove}>
          <Icon fontSize="small">close</Icon>
        </IconButton>
      </MDBox>
      <MDBox display="flex" flexWrap="wrap" alignItems="flex-start" gap={1} mt={1}>
        <GiftFields meta={meta} value={row} onChange={onChange} />
      </MDBox>
    </MDBox>
  );
}

CartRow.propTypes = {
  row: PropTypes.shape({
    type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    id: PropTypes.number,
    item: PropTypes.shape({}),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

/**
 * Dialog thêm quà: cột trái chọn loại, giữa chọn món (nhiều món một lần) hoặc nhập số, bên phải là
 * giỏ chỉnh được từng món; đổi loại vẫn giữ giỏ, bấm Thêm là đưa cả giỏ vào danh sách quà.
 */
function AddGiftDialog({ rows, onAdd, onClose }) {
  const [active, setActive] = useState(2);
  const [cart, setCart] = useState([]);
  const [settings, setSettings] = useState({});
  const meta = TYPE_BY_ID[active];
  const pick = PICK_SOURCES[active];
  const current = settings[active] || defaultsFor(active);
  const inCart = cart.filter((r) => r.type === active);

  const changeSettings = (patch) => {
    setSettings((prev) => ({ ...prev, [active]: { ...current, ...patch } }));
    setCart((prev) => prev.map((r) => (r.type === active ? fitRow({ ...r, ...patch }) : r)));
  };
  const updateRow = (index, patch) =>
    setCart((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const sameItem = (r, id) => r.type === active && r.id === id;
  const toggle = (x) => {
    const id = pickerId(pick.kind, x);
    setCart((prev) =>
      prev.some((r) => sameItem(r, id))
        ? prev.filter((r) => !sameItem(r, id))
        : [...prev, fitRow({ ...current, id, item: x })]
    );
  };
  const togglePage = (items, allPicked) =>
    setCart((prev) => {
      const ids = new Set(items.map((x) => pickerId(pick.kind, x)));
      if (allPicked) return prev.filter((r) => !(r.type === active && ids.has(r.id)));
      const have = new Set(prev.filter((r) => r.type === active).map((r) => r.id));
      return [
        ...prev,
        ...items
          .filter((x) => !have.has(pickerId(pick.kind, x)))
          .map((x) => fitRow({ ...current, id: pickerId(pick.kind, x), item: x })),
      ];
    });
  const toggleSimple = () =>
    setCart((prev) =>
      inCart.length ? prev.filter((r) => r.type !== active) : [...prev, { ...current }]
    );

  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Thêm quà
        <MDTypography variant="caption" color="text" display="block">
          Chọn loại bên trái rồi chọn món, đổi loại vẫn giữ giỏ. Chỉnh số lượng, cấp, hạn của từng
          món trong giỏ bên phải, bấm Thêm để đưa tất cả vào quà.
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        <MDBox display="flex" gap={2} flexDirection={{ xs: "column", lg: "row" }} minHeight={520}>
          <MDBox
            width={{ lg: 200 }}
            flexShrink={0}
            display="flex"
            flexDirection={{ xs: "row", lg: "column" }}
            flexWrap={{ xs: "wrap", lg: "nowrap" }}
            gap={0.25}
            sx={{ borderRight: { lg: "1px solid" }, borderColor: { lg: "divider" }, pr: { lg: 1 } }}
          >
            {ADD_GROUPS.map((group) => (
              <MDBox key={group.label} display="contents">
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  mt={1}
                  display={{ xs: "none", lg: "block" }}
                >
                  {group.label}
                </MDTypography>
                {GIFT_TYPES.filter((t) => !t.legacy && group.kinds.includes(t.kind)).map((t) => {
                  const selected = t.type === active;
                  const count = cart.filter((r) => r.type === t.type).length;
                  return (
                    <MDButton
                      key={t.type}
                      size="small"
                      variant={selected ? "gradient" : "text"}
                      color={selected ? "info" : "dark"}
                      onClick={() => setActive(t.type)}
                      sx={{ justifyContent: "flex-start", textTransform: "none", flexShrink: 0 }}
                    >
                      <GameIcon path={t.icon} size={18} fallback={t.fallback} />
                      &nbsp;{t.label}
                      {count > 0 && (
                        <Chip
                          size="small"
                          label={count}
                          color={selected ? "default" : "info"}
                          sx={{ ml: "auto", height: 18, fontSize: 11 }}
                        />
                      )}
                    </MDButton>
                  );
                })}
              </MDBox>
            ))}
          </MDBox>
          <MDBox flexGrow={1} minWidth={0}>
            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
              <GameIcon path={meta.icon} size={28} fallback={meta.fallback} />
              <MDBox lineHeight={1.2}>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {meta.label}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {meta.hint}
                </MDTypography>
              </MDBox>
            </MDBox>
            {pick && (
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Đặt chung cho mọi món {meta.label.toLowerCase()} trong giỏ và món chọn thêm:
              </MDTypography>
            )}
            <MDBox display="flex" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
              <GiftFields
                meta={meta}
                value={current}
                onChange={changeSettings}
                bulk={!!pick}
                bulkFusion={Math.max(0, ...inCart.map((r) => r.item?.maxFusion || 0))}
              />
              {!pick && (
                <MDButton
                  variant={inCart.length ? "outlined" : "gradient"}
                  color={inCart.length ? "error" : "info"}
                  onClick={toggleSimple}
                  sx={{ height: 44 }}
                >
                  <Icon>{inCart.length ? "remove" : "add"}</Icon>&nbsp;
                  {inCart.length ? "Bỏ khỏi giỏ" : "Cho vào giỏ"}
                </MDButton>
              )}
            </MDBox>
            {pick && (
              <PickerBrowser
                key={active}
                multiple
                filterAsSelect
                kind={pick.kind}
                itemType={pick.itemType ?? null}
                itemFilter={pick.itemFilter ?? null}
                selectedIds={new Set(inCart.map((r) => r.id))}
                existingIds={
                  new Set(rows.filter((r) => r.type === active && r.id).map((r) => Number(r.id)))
                }
                onToggle={toggle}
                onTogglePage={togglePage}
              />
            )}
            {meta.kind === "random" && (
              <MDTypography variant="caption" color="text" display="block">
                Cho vào giỏ rồi soạn danh sách món rút ở dòng quà sau khi thêm.
              </MDTypography>
            )}
          </MDBox>
          <MDBox
            width={{ lg: 380 }}
            flexShrink={0}
            display="flex"
            flexDirection="column"
            sx={{ borderLeft: { lg: "1px solid" }, borderColor: { lg: "divider" }, pl: { lg: 2 } }}
          >
            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
              <Icon color="info">shopping_basket</Icon>
              <MDTypography variant="button" fontWeight="medium">
                Giỏ quà: {cart.length} món
              </MDTypography>
              {cart.length > 0 && (
                <MDButton
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => setCart([])}
                  sx={{ ml: "auto" }}
                >
                  Bỏ hết
                </MDButton>
              )}
            </MDBox>
            {cart.length === 0 && (
              <MDTypography variant="caption" color="text">
                Chưa chọn món nào. Bấm vào món ở giữa để cho vào giỏ.
              </MDTypography>
            )}
            <MDBox flexGrow={1} maxHeight={{ lg: 560 }} overflow="auto" pr={0.5}>
              {cart.map((row, index) => (
                <CartRow
                  key={`${row.type}:${row.id ?? ""}`}
                  row={row}
                  onChange={(patch) => updateRow(index, patch)}
                  onRemove={() => setCart((prev) => prev.filter((r, i) => i !== index))}
                  onFocus={() => setActive(row.type)}
                />
              ))}
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Huỷ
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!cart.length}
          onClick={() => onAdd(cart)}
        >
          <Icon>add</Icon>&nbsp;Thêm {cart.length || ""} món vào quà
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

AddGiftDialog.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onAdd: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/** Một món quà dạng thẻ: icon + tên loại + ô chọn món + đủ ô nhập, không cần biết id hay JSON. */
function GiftRow({ row, onChange, onRemove, randomNote }) {
  const meta = TYPE_BY_ID[row.type];
  const pick = PICK_SOURCES[row.type];
  const header = meta || {
    label: "Quà đặc biệt",
    fallback: "data_object",
    hint: "Loại quà này chưa có form, sửa trực tiếp dạng JSON",
  };
  return (
    <MDBox
      border="1px solid"
      borderColor="divider"
      borderRadius="lg"
      px={1.5}
      py={1}
      mb={1}
      display="flex"
      alignItems="flex-start"
      gap={1}
    >
      <Tooltip title={header.hint} placement="top-start">
        <MDBox
          display="flex"
          alignItems="center"
          gap={1}
          minHeight={44}
          width={{ xs: 36, sm: 130 }}
          flexShrink={0}
        >
          <GameIcon path={header.icon} size={24} fallback={header.fallback} />
          <MDTypography
            variant="caption"
            fontWeight="medium"
            lineHeight={1.2}
            display={{ xs: "none", sm: "block" }}
          >
            {header.label}
          </MDTypography>
        </MDBox>
      </Tooltip>
      <MDBox flexGrow={1} minWidth={0}>
        {meta ? (
          <MDBox display="flex" flexWrap="wrap" alignItems="flex-start" gap={1}>
            {pick && (
              <MDBox flex="1 1 240px" minWidth={0}>
                <ModalPicker
                  kind={pick.kind}
                  itemType={pick.itemType ?? null}
                  itemFilter={pick.itemFilter ?? null}
                  value={row.id}
                  item={row.item}
                  onChange={(id, item) => onChange({ id, item })}
                  onLoad={(item) => item && !row.item && onChange({ item })}
                />
              </MDBox>
            )}
            <GiftFields meta={meta} value={row} onChange={onChange} />
            {meta.kind === "random" && (
              <MDBox display="flex" alignItems="center" minHeight={44} flex="1 1 200px">
                <MDTypography variant="caption" color="text">
                  {randomNote || "Người nhận được rút số lượt này trong danh sách bên dưới"}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        ) : (
          <MDInput
            fullWidth
            label="JSON"
            value={row.raw}
            error={toLine(row) === null}
            onChange={(event) => onChange({ raw: event.target.value })}
          />
        )}
        {meta?.kind === "random" && (
          <MDBox mt={1}>
            <RandomPool row={row} onChange={onChange} />
          </MDBox>
        )}
      </MDBox>
      <Tooltip title="Bỏ quà này">
        <IconButton color="error" onClick={onRemove}>
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    </MDBox>
  );
}

GiftRow.defaultProps = { randomNote: null };
GiftRow.propTypes = {
  row: PropTypes.shape({
    type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    raw: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    item: PropTypes.shape({}),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  randomNote: PropTypes.string,
};

/**
 * Soạn danh sách quà dạng [[loại, ...], ...] như gift_data của gift code, thư, boss.
 * onChange nhận mảng mới; dòng JSON nhập sai sẽ bị bỏ qua cho tới khi sửa đúng.
 */
function GiftEditor({ value, onChange, label, randomNote }) {
  const [rows, setRows] = useState(() => (value || []).map(toRow));
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState(() => JSON.stringify(value || []));
  const [rawError, setRawError] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const current = JSON.stringify(rows.map(toLine).filter(Boolean));
    if (JSON.stringify(value || []) !== current) {
      setRows((value || []).map(toRow));
      setRawText(JSON.stringify(value || []));
    }
  }, [value]);

  const commit = (nextRows) => {
    setRows(nextRows);
    const lines = nextRows.map(toLine).filter(Boolean);
    setRawText(JSON.stringify(lines));
    onChange(lines);
  };

  const update = (index, patch) =>
    commit(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const applyRaw = (text) => {
    setRawText(text);
    try {
      const parsed = JSON.parse(text.trim() || "[]");
      if (!Array.isArray(parsed) || parsed.some((line) => !Array.isArray(line))) {
        throw new Error("Phải là mảng các mảng số, xem JSON mẫu bên dưới");
      }
      const lines = parsed.map((line) => line.map(Number));
      if (lines.some((line) => line.some((n) => Number.isNaN(n)))) {
        throw new Error("Chỉ được chứa số");
      }
      setRawError(null);
      setRows(lines.map(toRow));
      onChange(lines);
    } catch (e) {
      setRawError(e.message);
    }
  };

  const lines = rows.map(toLine).filter(Boolean);

  const addFromDialog = (added) => {
    setAdding(false);
    commit([...rows, ...added]);
  };

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="button" fontWeight="medium">
          {label}
        </MDTypography>
        <Tooltip title={rawMode ? "Quay lại chọn quà bằng form" : "Dành cho người rành kỹ thuật"}>
          <MDButton
            variant="text"
            color="secondary"
            size="small"
            onClick={() => setRawMode(!rawMode)}
          >
            <Icon>{rawMode ? "view_list" : "code"}</Icon>&nbsp;
            {rawMode ? "Quay lại form" : "Nâng cao (JSON)"}
          </MDButton>
        </Tooltip>
      </MDBox>

      {rawMode ? (
        <>
          <MDInput
            fullWidth
            multiline
            minRows={4}
            value={rawText}
            onChange={(event) => applyRaw(event.target.value)}
            error={Boolean(rawError)}
            helperText={rawError || "Mảng các dòng quà, bỏ trống hoặc [] nếu không kèm quà"}
            placeholder={SAMPLE_TEXT}
            sx={{ fontFamily: "monospace" }}
          />
          <JsonHelp onInsertSample={() => applyRaw(SAMPLE_TEXT)} />
        </>
      ) : (
        <>
          {rows.length === 0 ? (
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              Chưa có quà nào.
            </MDTypography>
          ) : (
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <MDTypography variant="caption" color="text">
                {rows.length} món quà
              </MDTypography>
              {rows.length > 1 && (
                <MDButton variant="text" color="error" size="small" onClick={() => commit([])}>
                  <Icon>delete_sweep</Icon>&nbsp;Bỏ hết
                </MDButton>
              )}
            </MDBox>
          )}
          {rows.map((row, index) => (
            <GiftRow
              key={index}
              row={row}
              randomNote={randomNote}
              onChange={(patch) => update(index, patch)}
              onRemove={() => commit(rows.filter((r, i) => i !== index))}
            />
          ))}
          <MDButton variant="outlined" color="info" onClick={() => setAdding(true)}>
            <Icon>add_circle_outline</Icon>&nbsp;Thêm quà
          </MDButton>
        </>
      )}

      {adding && (
        <AddGiftDialog rows={rows} onAdd={addFromDialog} onClose={() => setAdding(false)} />
      )}

      {lines.length > 0 && (
        <MDBox mt={1.5}>
          <MDTypography variant="caption" color="text" display="block" mb={0.5}>
            Người nhận sẽ thấy:
          </MDTypography>
          <GiftPreview gift={lines} />
        </MDBox>
      )}
    </MDBox>
  );
}

GiftEditor.defaultProps = {
  value: [],
  label: "Quà tặng",
  randomNote: null,
};

GiftEditor.propTypes = {
  value: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  randomNote: PropTypes.string,
};

export default GiftEditor;
